import type { Selections } from './menuClient';

/**
 * Logique pure du panier — extraite pour être testable unitairement (US-206).
 * Aucune dépendance React/DOM : facile à couvrir par Vitest.
 */
export interface CartLine {
  name: string;
  unitPriceCents: number;
  quantity: number;
  /** Produit d'origine (B5) — permet de rouvrir la fiche pré-remplie pour modifier. */
  productId?: string;
  /** Choix d'options au moment de l'ajout (B5) — restaure la modale en édition. */
  selections?: Selections;
  /** Options groupées lisibles (parité ticket legacy) : [{group:'Viande', values:['Cordon Bleu']}]. */
  optionLines?: { group: string; values: string[] }[];
  /** Photo du produit (parité legacy §F-18) — affichée dans la ligne panier ; repli emoji si absente. */
  imageUrl?: string | null;
}

/** Remplace la ligne à l'index donné (B5 — édition d'une ligne au panier). Pur. */
export function replaceAt(lines: CartLine[], index: number, line: CartLine): CartLine[] {
  if (index < 0 || index >= lines.length) return lines;
  return lines.map((l, i) => (i === index ? line : l));
}

/** Total d'une ligne en centimes. */
export function lineTotalCents(line: CartLine): number {
  return line.unitPriceCents * line.quantity;
}

/** Sous-total panier en centimes. */
export function subtotalCents(lines: CartLine[]): number {
  return lines.reduce((sum, l) => sum + lineTotalCents(l), 0);
}

/**
 * Total à payer en centimes, frais de livraison inclus.
 * Les frais ne s'appliquent qu'en mode livraison (critère non-régression #12).
 */
export function totalCents(
  lines: CartLine[],
  opts: { deliveryFeeCents?: number; mode?: 'pickup' | 'delivery' } = {},
): number {
  const fee = opts.mode === 'delivery' ? (opts.deliveryFeeCents ?? 0) : 0;
  return subtotalCents(lines) + fee;
}

/** Le minimum de commande livraison est-il atteint ? (critère #12) */
export function meetsDeliveryMinimum(lines: CartLine[], minCents: number): boolean {
  return subtotalCents(lines) >= minCents;
}

/** Formate un montant en centimes selon la devise (€ ou CHF). */
export function formatPrice(cents: number, currency: 'EUR' | 'CHF' = 'EUR'): string {
  const amount = (cents / 100).toFixed(2);
  return currency === 'CHF' ? `${amount} CHF` : `${amount} €`;
}
